import React, { useState, useRef, useEffect } from 'react';
import { Send, Mic, MicOff, ChevronDown } from 'lucide-react';
import { MessageInputProps, SpeakingTone, TONE_OPTIONS } from './types';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useMobileDetection } from '../../hooks/useMobileDetection';

const MessageInput: React.FC<MessageInputProps> = ({ 
  onSendMessage, 
  isLoading, 
  placeholder = "Type your message..." 
}) => {
  const [message, setMessage] = useState('');
  const [selectedTone, setSelectedTone] = useState<SpeakingTone>('professional');
  const [showToneDropdown, setShowToneDropdown] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isMobile, deviceType } = useMobileDetection();
  
  // Debug logging
  useEffect(() => {
    console.log('[MessageInput] Mobile detection result:', { isMobile, deviceType });
  }, [isMobile, deviceType]);

  // Load saved tone on component mount
  useEffect(() => {
    const savedTone = localStorage.getItem('blp-speaking-tone') as SpeakingTone;
    if (savedTone && TONE_OPTIONS.find(opt => opt.value === savedTone)) {
      setSelectedTone(savedTone);
    }
  }, []);

  // Handle tone changes
  const handleToneChange = (tone: SpeakingTone) => {
    setSelectedTone(tone);
    localStorage.setItem('blp-speaking-tone', tone);
    setShowToneDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowToneDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Speech recognition hook
  const {
    isListening,
    transcript,
    interimTranscript,
    error: speechError,
    isSupported: isSpeechSupported,
    startListening,
    stopListening,
    clearTranscript,
    forceStopAndClear
  } = useSpeechRecognition();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const messageToSend = displayMessage.trim();
    
    if (messageToSend && !isLoading) {
      // If currently listening, force stop and clear everything immediately
      if (isListening) {
        forceStopAndClear();
      } else {
        // Just clear transcript if not listening
        clearTranscript();
      }
      
      // Clear typed message state
      setMessage('');
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      
      // Send the message with selected tone
      onSendMessage(messageToSend, selectedTone);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Update message field with speech transcript only when actively listening
  useEffect(() => {
    if (isListening && (transcript || interimTranscript)) {
      setMessage(transcript + interimTranscript);
    }
  }, [transcript, interimTranscript, isListening]);

  // Handle microphone button click
  const handleMicrophoneClick = () => {
    if (isListening) {
      stopListening();
    } else {
      clearTranscript();
      setMessage(''); // Clear existing message
      startListening();
    }
  };

  // Get the display message (prioritize typed message unless actively listening)
  const displayMessage = isListening ? (transcript + interimTranscript) : message;

  return (
    <div className="border-t border-dark-tertiary/30 bg-dark-secondary backdrop-blur-sm p-3 sm:p-4">
      {/* Speech Error Display */}
      {speechError && (
        <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {speechError}
        </div>
      )}
      
      {/* Tone Selector Dropdown */}
      <div className="mb-3">
        <div className="relative inline-block" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowToneDropdown(!showToneDropdown)}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 bg-dark-tertiary/50 hover:bg-dark-tertiary/70 
                       rounded-lg text-sm text-light-secondary transition-all duration-200
                       border border-dark-tertiary/30 hover:border-dark-tertiary/50
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-light-tertiary">Tone:</span>
            <span className="text-light-primary">{TONE_OPTIONS.find(opt => opt.value === selectedTone)?.label}</span>
            <ChevronDown 
              size={14} 
              className={`transition-transform duration-200 text-light-tertiary ${showToneDropdown ? 'rotate-180' : ''}`} 
            />
          </button>
          
          {showToneDropdown && (
            <div className="absolute bottom-full left-0 mb-1 w-72 sm:w-80 bg-dark-secondary border border-dark-tertiary/30 
                            rounded-lg shadow-xl z-50 overflow-hidden max-h-64 overflow-y-auto">
              {TONE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToneChange(option.value)}
                  className={`w-full text-left px-4 py-3 hover:bg-dark-tertiary/30 transition-all duration-150
                             border-b border-dark-tertiary/20 last:border-b-0 touch-manipulation
                             ${selectedTone === option.value ? 'bg-primary-500/10 text-primary-300' : 'text-light-secondary'}`}
                >
                  <div className="font-medium text-sm mb-1">{option.label}</div>
                  <div className="text-xs text-light-tertiary leading-relaxed">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="flex items-start gap-2 sm:gap-3 mobile-form-gap">
        <div className="flex-1">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={displayMessage}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? "Listening..." : placeholder}
              disabled={isLoading || isListening}
              className={`
                w-full px-4 py-3 rounded-xl resize-none
                text-light-primary placeholder-light-tertiary text-responsive
                focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed
                min-h-[50px] max-h-32 input-professional
                ${isListening ? 'border-2 border-primary-500/50 bg-primary-500/5' : ''}
                ${interimTranscript ? 'text-primary-300' : ''}
              `}
              rows={1}
            />
            
            {/* Character Counter */}
            {displayMessage.length > 0 && !isListening && (
              <div className="absolute bottom-2 right-2 text-xs text-light-tertiary">
                {displayMessage.length}
              </div>
            )}
          </div>
        </div>
        
        {/* Microphone Button - Hidden on mobile phones */}
        {isSpeechSupported && (
          <button
            type="button"
            onClick={handleMicrophoneClick}
            disabled={isLoading}
            className={`
              flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12
              rounded-xl transition-all duration-200 mt-1
              disabled:cursor-not-allowed disabled:text-light-tertiary
              shadow-lg hover:shadow-xl hover-lift
              active:scale-95 focus:ring-2 focus:ring-primary-500/50
              ${isListening 
                ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                : 'bg-dark-tertiary hover:bg-dark-tertiary/80 text-light-secondary hover:text-light-primary'
              }
              ${isMobile ? 'hidden' : ''}
              mobile-hide-mic
            `}
            title={isListening ? 'Stop recording' : 'Start voice input'}
          >
            {isListening ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        )}
        
        <button
          type="submit"
          disabled={!displayMessage.trim() || isLoading}
          className="
            flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12
            bg-primary-500 hover:bg-primary-600 disabled:bg-dark-tertiary
            text-white rounded-xl transition-all duration-200 mt-1
            disabled:cursor-not-allowed disabled:text-light-tertiary
            shadow-lg hover:shadow-xl hover-lift
            active:scale-95 focus:ring-2 focus:ring-primary-500/50
          "
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </form>
      
      <div className="flex items-start justify-between mt-3 mobile-optimize mobile-spacing">
        <div className="text-xs text-light-tertiary mobile-text flex-1 min-w-0 mr-4">
          <span className="hidden sm:inline">
            Press Enter to send, Shift+Enter for new line
            {isSpeechSupported && ' • Click mic for voice input'}
          </span>
          <span className="sm:hidden">Tap to send{isSpeechSupported && !isMobile && ' • Voice available'}</span>
        </div>
        
        {/* Copyright Notice */}
        <div className="text-xs text-light-tertiary hidden lg:block text-right max-w-xs">
          <div>Copyright © 2025 Listening Glass, LLC</div>
          <div>BLP is a registered trademark used with permission of</div>
          <div>Phil Graham, creator of the BLP.</div>
        </div>
      </div>
    </div>
  );
};

export default MessageInput;